import {useCustomPreferences} from '../context/preferences'
import {useGlobalCustomPreferences} from '../context/global-preferences'
import {useLocaleCountry} from './use-locale-custom'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'

/**
 * Retrieves a specific custom preference value based on the provided key and locale.
 *
 * @param {Array} preferences - The array of custom preferences.
 * @param {string} key - The key of the custom preference to retrieve.
 * @param {string} locale - The current locale.
 * @returns {any} - The value of the custom preference for the given locale, or null if not found.
 */
const getPreferenceValue = (preferences, key, locale) => {
    const preference = preferences.find((pref) => pref.id === key)
    try {
        return preference ? JSON.parse(preference.value)?.[locale] ?? null : null
    } catch (error) {
        console.error('getPreferenceValue - Malformed JSON', key, locale, error)
        return null
    }
}

/**
 * Retrieves a specific custom preference value based on the provided key and locale.
// ...existing code...
    }
}

/**
 * Retrieves a preference value by first checking for a locale-specific key (e.g., "fr-be")
 * and falling back to a country-specific key (e.g., "be").
 *
 * @param {Array} preferences - The array of custom preferences.
 * @param {string} key - The key of the custom preference to retrieve.
 * @param {string} country - The current country code.
 * @param {string} localeAlias - The current locale alias (e.g., "fr-be").
 * @returns {any} - The value of the custom preference, or null if not found.
 */
const getPreferenceWithFallback = (preferences, key, country, localeAlias) => {
    const preference = preferences.find((pref) => pref.id === key)
    if (!preference) {
        return null
    }
    try {
        const values = JSON.parse(preference.value)
        if (!values) {
            return null
        }
        // Create case-insensitive versions of keys for robust matching.
        const localeKey = localeAlias.toLowerCase()
        const countryKey = country.toLowerCase()

        // Find the matching key in the preference object, ignoring case.
        const foundLocaleKey = Object.keys(values).find((k) => k.toLowerCase() === localeKey)
        if (foundLocaleKey && values[foundLocaleKey]) {
            return values[foundLocaleKey]
        }

        const foundCountryKey = Object.keys(values).find((k) => k.toLowerCase() === countryKey)
        if (foundCountryKey && values[foundCountryKey]) {
            return values[foundCountryKey]
        }

        return null
    } catch (error) {
        console.error('getPreferenceWithFallback - Malformed JSON', key, error)
        return null
    }
}

const useCustomPreferenceWithFallback = (key) => {
    const customPreferences = useCustomPreferences()
    const {locale} = useMultiSite()
    const country = useLocaleCountry()
    return getPreferenceWithFallback(customPreferences, key, country, locale.alias)
}

const getMarketlessPreferenceValue = (preferences, key) => {
    const preference = preferences.find((pref) => pref.id === key)
    try {
        return preference ? JSON.parse(preference.value) : null
    } catch (error) {
        console.error('getMarketlessPreferenceValue - Malformed JSON', key, error)
        return null
    }
}

const useCustomPreference = (key) => {
    const customPreferences = useCustomPreferences()
    const currentCountry = useLocaleCountry().toLowerCase()
    return getPreferenceValue(customPreferences, key, currentCountry)
}

const useBooleanCustomPreference = (key) => {
    const value = useCustomPreference(key)
    return value === 'true' ? true : value === 'false' ? false : null
}

const useCustomPreferenceWithLocale = (key) => {
    const customPreferences = useCustomPreferences()
    const {locale} = useMultiSite()
    return getPreferenceValue(customPreferences, key, locale.alias)
}

const useCustomPreferenceMarketless = (key) => {
    const customPreferences = useCustomPreferences()
    return getMarketlessPreferenceValue(customPreferences, key)
}

const getGlobalPreferenceValue = (preferences, key) => {
    const preference = preferences.find((pref) => pref.id === key)
    try {
        return preference ? JSON.parse(preference.value) : null
    } catch (error) {
        console.error('getPreferenceValue - Malformed JSON', key, error)
        return null
    }
}

const useGlobalCustomPreference = (key) => {
    const globalCustomPreferences = useGlobalCustomPreferences()
    return getGlobalPreferenceValue(globalCustomPreferences, key)
}

export {
    useCustomPreference,
    useBooleanCustomPreference,
    useCustomPreferenceWithLocale,
    useCustomPreferenceMarketless,
    useGlobalCustomPreference,
    useCustomPreferenceWithFallback,
}
